import { Helmet } from "react-helmet-async";
import Seo from "@/components/Seo";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { BEFORE_AFTER } from "@/lib/content";
import { Link } from "react-router-dom";

export default function ResultsPage() {
    return (
        <div className="page-fade pt-32 md:pt-40 pb-24">
            <Seo
                title="Aqualyx Before & After Results — NZ Body Contouring Gallery"
                description="Authentic Aqualyx before & after results from Certified Clinics in New Zealand. Double chin, jawline, abdomen, thigh, knee, flank and bra-line contouring. Individual results may vary."
                path="/results"
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Results", path: "/results" },
                ]}
                schema={{
                    "@context": "https://schema.org",
                    "@type": "ImageGallery",
                    "name": "Aqualyx Before & After — New Zealand",
                    "description": "Authentic patient before & after cases from Aqualyx Certified Clinics across New Zealand.",
                    "image": BEFORE_AFTER.map((b) => ({
                        "@type": "ImageObject",
                        "url": b.src,
                        "name": `${b.title} — ${b.sessions}`,
                        "description": `${b.area} — ${b.sessions}`
                    }))
                }}
            />
            <Helmet>
                <meta name="keywords" content="Aqualyx before after, Aqualyx results NZ, double chin before after, jawline contouring before after, abdomen Aqualyx, body contouring results New Zealand" />
            </Helmet>
            <section className="max-w-[1400px] mx-auto px-6 md:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
                    <div className="lg:col-span-8 space-y-6">
                        <div className="eyebrow">The Gallery</div>
                        <h1 className="editorial-headline text-6xl md:text-8xl">
                            Before<br /><span className="italic text-champagne">& After.</span>
                        </h1>
                    </div>
                    <div className="lg:col-span-4 flex items-end">
                        <p className="text-ink/70 leading-relaxed">
                            Authentic patient cases from Aqualyx Certified Clinics across New Zealand. Each comparison shows progression after a specified course of treatments. Individual results may vary.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14">
                    {BEFORE_AFTER.map((b, i) => (
                        <div key={b.title} data-testid={`results-card-${i}`} className="lg:col-span-8 lg:col-start-3 space-y-5">
                            <BeforeAfterSlider src={b.src} layout={b.layout} alt={b.title} />
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div>
                                    <div className="font-serif text-3xl">{b.title}</div>
                                    <div className="eyebrow mt-1">{b.area} · {b.sessions}</div>
                                </div>
                                <div className="eyebrow text-ink/40">Individual results may vary</div>
                            </div>
                        </div>
                    ))}

                    <div className="lg:col-span-8 lg:col-start-3 border border-champagne/40 bg-nude p-10 text-center space-y-4">
                        <div className="eyebrow text-champagne">Patient Case Library</div>
                        <h3 className="font-serif text-3xl md:text-4xl leading-tight max-w-2xl mx-auto">
                            A wider selection of before & after results is shared in confidence during your in-clinic consultation.
                        </h3>
                        <p className="text-sm text-ink/60">For patient privacy, additional case photography is not published online.</p>
                        <a href="https://www.instagram.com/aqualyx_nz/" target="_blank" rel="noreferrer" className="btn-outline inline-flex">Follow @aqualyx_nz on Instagram</a>
                    </div>
                </div>

                <div className="mt-20 border-t border-champagne/30 pt-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                    <div>
                        <div className="eyebrow mb-4">Reference Imagery</div>
                        <h2 className="font-serif text-4xl md:text-5xl leading-tight">Body & Face contouring imagery from the global Aqualyx archive.</h2>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <img src="https://dermalis.co.nz/aqualyx-results.png" alt="Aqualyx results" className="w-full aspect-square object-cover" />
                        <img src="https://dermalis.co.nz/aqualyx-face-results.png" alt="Aqualyx Face Kit results" className="w-full aspect-square object-cover" />
                    </div>
                </div>

                <div className="mt-16 text-center space-y-4">
                    <p className="eyebrow text-ink/50">Disclaimer · Individual results may vary</p>
                    <Link to="/clinics" className="btn-primary">Find a Certified Clinic</Link>
                </div>
            </section>
        </div>
    );
}
